import React, { useEffect, useMemo, useState, useCallback } from "react";
import GroupChannelList from "@sendbird/uikit-react/GroupChannelList";
import GroupChannel from "@sendbird/uikit-react/GroupChannel";
import { HandshakeIcon, MessagesSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSendbird } from "@/hooks/useSendbird";
import { api } from "@/api/client";

/**
 * SendbirdChannelPanel — reusable "channel list + open thread" panel for
 * admin/pharmacist/support/doctor dashboards.
 *
 * Props:
 *   - customType (string, required): one of rx_verification|support_ticket|doctor_consult
 *   - claimEndpoint (fn -> string, required): given a channel, returns `/api/...` URL to POST claim to
 *   - claimButtonLabel (string): label for the claim button
 *   - showUnclaimedBadge (bool): show "N unassigned" badge in header
 *   - unassignedEntityType (string): backend `entity_type` value to count unassigned for the header badge
 *   - height (string)
 *   - testId (string)
 *   - onClaimSuccess ((channel) => void): callback after successful claim
 */
const CUSTOM_TYPE_INFO = {
    rx_verification: { title: "Live Rx Chats", subtitle: "Customer prescriptions awaiting pharmacist review", tone: "emerald", entityType: "prescription" },
    support_ticket: { title: "Live Support Chats", subtitle: "Escalated customer tickets", tone: "amber", entityType: "support_ticket" },
    doctor_consult: { title: "Live Consults", subtitle: "Active doctor consultations", tone: "sky", entityType: "consultation" },
};

const SendbirdChannelPanel = ({
    customType,
    claimEndpoint,
    claimButtonLabel = "Claim",
    showUnclaimedBadge = true,
    unassignedEntityType,
    height = "620px",
    testId,
    onClaimSuccess,
}) => {
    const { ready, session } = useSendbird();
    const [selected, setSelected] = useState(null);
    const [claiming, setClaiming] = useState(false);
    const [claimedIds, setClaimedIds] = useState(new Set());
    const [unassignedCount, setUnassignedCount] = useState(null);
    const info = CUSTOM_TYPE_INFO[customType] || CUSTOM_TYPE_INFO.rx_verification;
    const entityType = unassignedEntityType || info.entityType;

    const queryParams = useMemo(() => ({
        customTypesFilter: [customType],
        limit: 40,
        includeEmpty: true,
        order: "latest_last_message",
    }), [customType]);

    // Poll unassigned count so the badge doesn't go stale.
    const refreshUnassignedCount = useCallback(async () => {
        if (!showUnclaimedBadge || !entityType) return;
        try {
            const { data } = await api.get("/admin/sendbird/channels", {
                params: { entity_type: entityType, unassigned: true, limit: 1 },
            });
            setUnassignedCount(data?.total ?? 0);
        } catch (err) {
            setUnassignedCount(null);
        }
    }, [showUnclaimedBadge, entityType]);

    useEffect(() => {
        if (!ready) return undefined;
        refreshUnassignedCount();
        const id = setInterval(refreshUnassignedCount, 30_000);
        return () => clearInterval(id);
    }, [ready, refreshUnassignedCount]);

    const isClaimed = useCallback((channel) => {
        if (!channel || !session) return false;
        if (claimedIds.has(channel.url)) return true;
        // A channel is "claimed" for our purposes when the current staff user
        // is a member (not just the pool user).
        const members = channel.members || [];
        return members.some((m) => m.userId === session.user_id);
    }, [session, claimedIds]);

    const handleClaim = async () => {
        if (!selected) return;
        setClaiming(true);
        try {
            const url = claimEndpoint(selected);
            await api.post(url, {});
            setClaimedIds((prev) => new Set(prev).add(selected.url));
            toast.success("Channel claimed — you can now respond as staff.");
            onClaimSuccess?.(selected);
            refreshUnassignedCount();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Could not claim channel");
        } finally {
            setClaiming(false);
        }
    };

    if (!ready) {
        return (
            <div
                className="grid place-items-center rounded-2xl border border-slate-200 bg-white p-10 text-slate-500"
                style={{ height }}
                data-testid={testId ? `${testId}-loading` : undefined}
            >
                <div className="flex items-center gap-2 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Connecting to Sendbird…
                </div>
            </div>
        );
    }

    return (
        <div
            className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 lg:grid-cols-[320px_1fr] dark:border-white/10 dark:bg-white/[0.02]"
            style={{ height }}
            data-testid={testId}
        >
            {/* Left rail — channel list */}
            <div className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft dark:border-white/10 dark:bg-white/[0.03]">
                <header className="flex items-start justify-between gap-2 border-b border-slate-100 px-4 py-3 dark:border-white/10">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{info.subtitle}</p>
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-zinc-100">{info.title}</h3>
                    </div>
                    {showUnclaimedBadge && unassignedCount != null && unassignedCount > 0 && (
                        <span
                            className="whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800"
                            data-testid={`${testId}-unassigned-badge`}
                            title="Refreshes every 30 seconds"
                        >
                            {unassignedCount} unassigned
                        </span>
                    )}
                </header>
                <div className="min-h-0 flex-1 overflow-y-auto" data-testid={`${testId}-list`}>
                    <GroupChannelList
                        channelListQueryParams={queryParams}
                        onChannelSelect={(ch) => setSelected(ch)}
                        selectedChannelUrl={selected?.url}
                        renderHeader={() => null}
                    />
                </div>
            </div>

            {/* Right — active channel */}
            <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft dark:border-white/10 dark:bg-white/[0.03]" data-testid={`${testId}-active`}>
                {!selected ? (
                    <div className="grid h-full place-items-center px-6 text-center text-slate-500">
                        <div>
                            <MessagesSquare className="mx-auto h-8 w-8 text-brand-400" />
                            <p className="mt-3 text-sm">Select a channel on the left to view the conversation.</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Claim banner — visible when current staff isn't a channel member yet */}
                        {!isClaimed(selected) && (
                            <div className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-xs dark:border-amber-500/30 dark:bg-amber-500/10" data-testid={`${testId}-claim-banner`}>
                                <span className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                                    <HandshakeIcon className="h-3.5 w-3.5" />
                                    You&apos;re viewing this channel as a spectator. Claim it to respond.
                                </span>
                                <button
                                    onClick={handleClaim}
                                    disabled={claiming}
                                    className="rounded-lg bg-amber-600 px-3 py-1 text-[11px] font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                                    data-testid={`${testId}-claim-btn`}
                                >
                                    {claiming ? "Claiming…" : claimButtonLabel}
                                </button>
                            </div>
                        )}
                        <div className="min-h-0 flex-1">
                            <GroupChannel channelUrl={selected.url} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SendbirdChannelPanel;
